import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, CheckCircle2, Globe, HelpCircle, Copy, X, Check } from 'lucide-react';
import axios from 'axios';
import { collectDiagnostics, formatDiagnostics } from '../lib/supportDiagnostics';

const LOGO = 'https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/q2sfm1zu_logo_white.png';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRANSLATIONS = {
  it: {
    back: 'Indietro',
    title: 'Assistenza',
    subtitle: 'Hai bisogno di aiuto? Scrivici e ti rispondiamo via email.',
    name: 'Nome',
    namePlaceholder: 'Il tuo nome',
    email: 'Email',
    emailPlaceholder: 'tu@esempio.com',
    subject: 'Oggetto',
    subjectPlaceholder: 'Di cosa hai bisogno?',
    message: 'Messaggio (opzionale)',
    messagePlaceholder: 'Descrivi la tua richiesta...',
    submit: 'Invia richiesta',
    sending: 'Invio in corso...',
    successTitle: 'Richiesta inviata',
    successMsg: 'Grazie! Ti risponderemo via email il prima possibile.',
    sendAnother: 'Invia un\'altra richiesta',
    errorRequired: 'Compila tutti i campi obbligatori.',
    errorEmail: 'Inserisci un indirizzo email valido.',
    errorGeneric: 'Si è verificato un errore. Riprova.',
    languageLabel: 'Lingua',
  },
  en: {
    back: 'Back',
    title: 'Support',
    subtitle: 'Need help? Write to us and we\'ll reply via email.',
    name: 'Name',
    namePlaceholder: 'Your name',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    subject: 'Subject',
    subjectPlaceholder: 'What do you need?',
    message: 'Message (optional)',
    messagePlaceholder: 'Describe your request...',
    submit: 'Send request',
    sending: 'Sending...',
    successTitle: 'Request sent',
    successMsg: 'Thanks! We\'ll reply via email as soon as possible.',
    sendAnother: 'Send another request',
    errorRequired: 'Please fill in all required fields.',
    errorEmail: 'Please enter a valid email address.',
    errorGeneric: 'Something went wrong. Please try again.',
    languageLabel: 'Language',
  },
  es: {
    back: 'Atrás',
    title: 'Asistencia',
    subtitle: '¿Necesitas ayuda? Escríbenos y te responderemos por email.',
    name: 'Nombre',
    namePlaceholder: 'Tu nombre',
    email: 'Email',
    emailPlaceholder: 'tu@ejemplo.com',
    subject: 'Asunto',
    subjectPlaceholder: '¿Qué necesitas?',
    message: 'Mensaje (opcional)',
    messagePlaceholder: 'Describe tu solicitud...',
    submit: 'Enviar solicitud',
    sending: 'Enviando...',
    successTitle: 'Solicitud enviada',
    successMsg: '¡Gracias! Te responderemos por email lo antes posible.',
    sendAnother: 'Enviar otra solicitud',
    errorRequired: 'Por favor completa todos los campos obligatorios.',
    errorEmail: 'Introduce un email válido.',
    errorGeneric: 'Algo salió mal. Inténtalo de nuevo.',
    languageLabel: 'Idioma',
  },
  pt: {
    back: 'Voltar',
    title: 'Suporte',
    subtitle: 'Precisa de ajuda? Escreva-nos e responderemos por email.',
    name: 'Nome',
    namePlaceholder: 'Seu nome',
    email: 'Email',
    emailPlaceholder: 'voce@exemplo.com',
    subject: 'Assunto',
    subjectPlaceholder: 'Do que você precisa?',
    message: 'Mensagem (opcional)',
    messagePlaceholder: 'Descreva sua solicitação...',
    submit: 'Enviar solicitação',
    sending: 'Enviando...',
    successTitle: 'Solicitação enviada',
    successMsg: 'Obrigado! Responderemos por email assim que possível.',
    sendAnother: 'Enviar outra solicitação',
    errorRequired: 'Preencha todos os campos obrigatórios.',
    errorEmail: 'Insira um email válido.',
    errorGeneric: 'Algo deu errado. Tente novamente.',
    languageLabel: 'Idioma',
  },
  de: {
    back: 'Zurück',
    title: 'Support',
    subtitle: 'Brauchst du Hilfe? Schreib uns und wir antworten per E-Mail.',
    name: 'Name',
    namePlaceholder: 'Dein Name',
    email: 'E-Mail',
    emailPlaceholder: 'du@beispiel.com',
    subject: 'Betreff',
    subjectPlaceholder: 'Was brauchst du?',
    message: 'Nachricht (optional)',
    messagePlaceholder: 'Beschreibe deine Anfrage...',
    submit: 'Anfrage senden',
    sending: 'Wird gesendet...',
    successTitle: 'Anfrage gesendet',
    successMsg: 'Danke! Wir antworten so schnell wie möglich per E-Mail.',
    sendAnother: 'Weitere Anfrage senden',
    errorRequired: 'Bitte fülle alle Pflichtfelder aus.',
    errorEmail: 'Bitte gib eine gültige E-Mail-Adresse ein.',
    errorGeneric: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
    languageLabel: 'Sprache',
  },
  fr: {
    back: 'Retour',
    title: 'Assistance',
    subtitle: 'Besoin d\'aide ? Écrivez-nous et nous vous répondrons par email.',
    name: 'Nom',
    namePlaceholder: 'Votre nom',
    email: 'Email',
    emailPlaceholder: 'vous@exemple.com',
    subject: 'Objet',
    subjectPlaceholder: 'De quoi avez-vous besoin ?',
    message: 'Message (facultatif)',
    messagePlaceholder: 'Décrivez votre demande...',
    submit: 'Envoyer la demande',
    sending: 'Envoi...',
    successTitle: 'Demande envoyée',
    successMsg: 'Merci ! Nous répondrons par email dès que possible.',
    sendAnother: 'Envoyer une autre demande',
    errorRequired: 'Veuillez remplir tous les champs obligatoires.',
    errorEmail: 'Veuillez entrer une adresse email valide.',
    errorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
    languageLabel: 'Langue',
  },
};

const LANGS = [
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
];

function detectLang() {
  try {
    const stored = localStorage.getItem('landing_lang');
    if (stored && TRANSLATIONS[stored]) return stored;
    const browser = (navigator.language || 'it').slice(0, 2).toLowerCase();
    if (TRANSLATIONS[browser]) return browser;
  } catch (_) {
    // ignore
  }
  return 'it';
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function PublicSupport() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(detectLang);
  const [langOpen, setLangOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const t = useMemo(() => TRANSLATIONS[lang] || TRANSLATIONS.it, [lang]);

  useEffect(() => {
    document.documentElement.classList.remove('mobile-app');
    try {
      localStorage.setItem('landing_lang', lang);
    } catch (_) { /* ignore */ }
  }, [lang]);

  const onChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (error) setError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const name = form.name.trim();
    const email = form.email.trim();
    const subject = form.subject.trim();
    if (!name || !email || !subject) {
      setError(t.errorRequired);
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError(t.errorEmail);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/support/public-request`, {
        name,
        email,
        subject,
        message: form.message.trim(),
        language: lang,
      });
      setSuccess(true);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(t.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080D1A] text-white flex flex-col" data-testid="public-support-page">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-[#080D1A] sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-gray-300 hover:text-white transition-colors"
            data-testid="public-support-back-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            {t.back}
          </button>
          <Link to="/" aria-label="Slimmy">
            <img src={LOGO} alt="Slimmy" className="h-6" />
          </Link>
          <div className="relative flex items-center gap-2">
            {/* Diagnostic info popover */}
            <DiagnosticsButton />
            <div className="relative">
              <button
                type="button"
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/5"
                data-testid="public-support-lang-btn"
                aria-label={t.languageLabel}
              >
                <Globe className="w-4 h-4" />
                <span className="uppercase">{lang}</span>
              </button>
              {langOpen && (
                <div
                  className="absolute right-0 mt-2 w-44 bg-[#0F1424] border border-white/10 rounded-lg shadow-2xl py-1 z-30"
                  data-testid="public-support-lang-menu"
                >
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-white/5 ${
                        l.code === lang ? 'text-white' : 'text-gray-300'
                      }`}
                      data-testid={`public-support-lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 px-4 py-10">
        <div className="max-w-xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3" data-testid="public-support-title">
            {t.title}
          </h1>
          <p className="text-gray-400 mb-8">{t.subtitle}</p>

          {success ? (
            <div
              className="rounded-2xl bg-emerald-500/10 border border-emerald-400/20 p-6 flex flex-col items-start gap-3"
              data-testid="public-support-success"
            >
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              <h2 className="text-xl font-semibold text-white">{t.successTitle}</h2>
              <p className="text-gray-300">{t.successMsg}</p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-2 text-sm text-emerald-300 hover:text-emerald-200 underline-offset-4 hover:underline"
                data-testid="public-support-send-another-btn"
              >
                {t.sendAnother}
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="public-support-form">
              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1.5" htmlFor="ps-name">
                  {t.name} *
                </label>
                <input
                  id="ps-name"
                  type="text"
                  value={form.name}
                  onChange={onChange('name')}
                  placeholder={t.namePlaceholder}
                  required
                  maxLength={200}
                  className="w-full rounded-lg bg-[#0F1424] border border-white/10 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40"
                  data-testid="public-support-name-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1.5" htmlFor="ps-email">
                  {t.email} *
                </label>
                <input
                  id="ps-email"
                  type="email"
                  value={form.email}
                  onChange={onChange('email')}
                  placeholder={t.emailPlaceholder}
                  required
                  maxLength={200}
                  className="w-full rounded-lg bg-[#0F1424] border border-white/10 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40"
                  data-testid="public-support-email-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1.5" htmlFor="ps-subject">
                  {t.subject} *
                </label>
                <input
                  id="ps-subject"
                  type="text"
                  value={form.subject}
                  onChange={onChange('subject')}
                  placeholder={t.subjectPlaceholder}
                  required
                  maxLength={200}
                  className="w-full rounded-lg bg-[#0F1424] border border-white/10 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40"
                  data-testid="public-support-subject-input"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1.5" htmlFor="ps-message">
                  {t.message}
                </label>
                <textarea
                  id="ps-message"
                  value={form.message}
                  onChange={onChange('message')}
                  placeholder={t.messagePlaceholder}
                  rows={5}
                  maxLength={2000}
                  className="w-full rounded-lg bg-[#0F1424] border border-white/10 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40 resize-y"
                  data-testid="public-support-message-input"
                />
              </div>

              {error && (
                <div
                  className="rounded-lg bg-red-500/10 border border-red-400/20 px-4 py-3 text-sm text-red-300"
                  data-testid="public-support-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 transition-colors"
                data-testid="public-support-submit-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? t.sending : t.submit}
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-white/5 py-6 px-4 text-center text-xs text-gray-500">
        <div>NOVA SERVICE FCO S.R.L.</div>
        <div>VIA DELLA FOCE MICINA 10/L - 00054 - FIUMICINO (ROMA) - ITALY</div>
        <div>Partita IVA: 17857241008 - Codice Fiscale: 17857241008</div>
      </footer>
    </div>
  );
}

/**
 * Round "?" button next to the language switcher. On click opens a
 * popup with the build version, native bridge state, UA, screen info
 * and a "Copy" button that copies the info to clipboard for pasting
 * into a support request.
 */
function DiagnosticsButton() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const info = useMemo(() => (open ? collectDiagnostics() : null), [open]);

  const doCopy = async () => {
    if (!info) return;
    const text = formatDiagnostics(info);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older WebViews
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* clipboard unavailable */ }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-white/15 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
        data-testid="diagnostics-open-btn"
        aria-label="App info"
        title="App info"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {open && info && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="diagnostics-modal"
        >
          <div
            className="bg-[#0F1424] border border-white/10 rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <h3 className="text-base font-semibold text-white">App info</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md hover:bg-white/5 text-gray-400"
                data-testid="diagnostics-close-btn"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-2 text-xs font-mono text-gray-300">
              {Object.entries(info).map(([k, v]) => (
                <div key={k} className="break-all">
                  <span className="text-gray-500">{k}:</span>{' '}
                  <span className="text-gray-100">{String(v)}</span>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={doCopy}
                data-testid="diagnostics-copy-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#E8334A] text-white text-sm font-medium hover:bg-[#d12d42] transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copiato!' : 'Copia info'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
